import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { GlassCard } from '@/components/ui/GlassCard'
import { Icon } from '@/components/ui/Icon'
import type { Me } from '@/lib/types/me'
import { Package, Plus } from '@/lib/icons'

/** Matches api `listing_posts_per_window`. */
const POSTS_PER_WINDOW = 1

export function StashPointsPanel({
  me,
  linkCount,
}: {
  me: Me
  linkCount: number
}) {
  const postsUsed = Math.min(me.postsUsed, POSTS_PER_WINDOW)
  const progress = (postsUsed / POSTS_PER_WINDOW) * 100

  return (
    <section
      aria-label="Your points"
      className="border-glass-border bg-surface-container-high/50 relative overflow-hidden rounded-xl border p-6 shadow-lg backdrop-blur-sm md:p-8"
    >
      <div
        aria-hidden="true"
        className="bg-primary-container/10 absolute top-0 right-0 h-64 w-64 translate-x-1/3 -translate-y-1/2 rounded-full blur-3xl"
      />
      <div className="relative z-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <div className="flex flex-col gap-2">
          {(!me.emailVerified || me.status !== 'active') && (
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {!me.emailVerified && (
                <span className="font-body text-label-caps text-secondary border-glass-border rounded-full border px-3 py-1 uppercase">
                  Verify email
                </span>
              )}
              {me.status !== 'active' && (
                <span className="font-body text-label-caps text-error border-glass-border rounded-full border px-3 py-1 uppercase">
                  Suspended
                </span>
              )}
            </div>
          )}
          <h1 className="font-display text-display-xl text-fg tracking-tight">
            {me.points}{' '}
            <span className="font-display text-headline-lg-mobile text-fg-muted font-normal">
              PTS
            </span>
          </h1>
          <p className="font-body text-body-sm text-fg-muted">
            {linkCount === 0
              ? 'Post links to start earning points.'
              : `${linkCount} link${linkCount === 1 ? '' : 's'} in your stash.`}
          </p>
        </div>

        <div className="z-10 flex w-full flex-col gap-3 md:w-1/3">
          <div className="font-body text-label-caps text-on-surface-variant flex items-end justify-between uppercase">
            <span>Posts used</span>
            <span>
              {postsUsed}/{POSTS_PER_WINDOW}
            </span>
          </div>
          <div className="bg-surface-container-highest h-2 w-full overflow-hidden rounded-full">
            <div
              className="bg-primary-container h-full rounded-full transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export function StashEmptyLinks() {
  return (
    <GlassCard className="flex flex-col items-center gap-6 p-8 text-center">
      <div className="border-glass-border bg-surface-container flex size-24 items-center justify-center rounded-xl border">
        <Icon icon={Package} className="text-primary size-12" />
      </div>
      <div className="space-y-2">
        <h3 className="font-display text-headline-lg-mobile text-fg">
          Your stash is empty
        </h3>
        <p className="font-body text-body-md text-fg-muted mx-auto max-w-sm">
          Post a referral or discount link and start earning points every time
          someone claims it.
        </p>
      </div>
      <Button href="/drop" variant="gradient" size="md">
        <Icon icon={Plus} className="size-4" />
        Post your first link
      </Button>
      <p className="font-body text-label-sm text-fg-muted">
        Or{' '}
        <Link
          to="/discover"
          className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
        >
          browse live links
        </Link>{' '}
        first.
      </p>
    </GlassCard>
  )
}
